import React, {
  CSSProperties,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import KeyboardTabIcon from '@mui/icons-material/KeyboardTab';
import dayjs, { Dayjs } from 'dayjs';
import clsx from 'clsx';
import { TaskItem } from '@demind-inc/core';
import { useDrop } from 'react-dnd';
import { useTranslation } from 'react-i18next';

import './KanbanList.scss';
import { isAppleRemindersDesktopSupported } from '../../../helpers/appleRemindersDesktop.helpers';
import { KanbanTaskCard } from '../KanbanTaskCard';
import { TaskCreateCard } from '../TaskCreateCard';
import {
  useAuthContext,
  useCircadianContext,
  useLanguageContext,
  useTodoTasksContext,
  AddTodoTaskItemProps,
  DeleteTaskMutateParam,
  isAssignedTeamTask,
} from '../../../data-access';
import { useProjectFilter, useTaskSettings } from '../../../hooks';
import { CircularProgress, IconButton, Stack, Typography } from '@mui/material';
import { CustomTooltip } from '../../common';
import { trackEventMixpanel } from '../../../utils';
import { AddOutlined } from '@mui/icons-material';
import { KanbanListFilterItem, KanbanListFilterMenu } from './KanbanListFilterMenu';
import {
  calculateNewSortIndex,
  getDueDateYYYYMMDD,
  getIsOverdue,
  SORT_INDEX_INCREMENT,
  getFormattedDueDateForAddTask,
  getStartDateTimeFromDuration,
} from '../../../helpers';
import { last } from 'lodash';
import { translateMinToHHhMMmString } from '../../../data-access/helpers/date.helpers';
import { getDayjsLocaleForLanguage } from '../../../i18n/dayjs';

interface KanbanListProps {
  date: Dayjs;
  tasks: TaskItem[];
  isFetchingTasks?: boolean;
  filterOptions?: KanbanListFilterItem[];
  isOverDue?: boolean;
  isDemo?: boolean;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
  onClickTask: (task: TaskItem) => void;
  onMoveTask: (task: TaskItem, sortIndex?: number) => void;
  onUpdateTask?: (
    taskId: string,
    boardId: string,
    updates: Partial<TaskItem>,
    shouldReflectToEvent?: boolean
  ) => Promise<void>;
  onCreateTask?: (options: AddTodoTaskItemProps) => Promise<void>;
  onDeleteTask?: (task: TaskItem, mutateParam?: DeleteTaskMutateParam) => void;
  onCompleteTask?: (
    task: TaskItem,
    taskId: string,
    completed: boolean,
    mutateParams?: {
      date?: string;
      parentOriginalTaskId?: string;
    }
  ) => Promise<void> | void;
  className?: string;
  style?: CSSProperties;
  addingTaskDate: string | null;
  setAddingTaskDate: (date: string | null) => void;
}

const DEMO_TASK_PROJECT_NAME = 'Biz';

export const KanbanList: React.FC<KanbanListProps> = memo(
  ({
    date,
    tasks,
    isFetchingTasks = false,
    onMoveTask,
    className,
    isOverDue,
    isDemo,
    isCollapsed = false,
    onToggleCollapse,
    filterOptions = [],
    onClickTask,
    onUpdateTask,
    onCreateTask = () => void 0,
    onDeleteTask = () => void 0,
    onCompleteTask = () => void 0,
    style,
    addingTaskDate,
    setAddingTaskDate,
  }) => {
    const { t } = useTranslation();
    const { language } = useLanguageContext();
    const listRef = useRef<HTMLDivElement>(null);
    const tasksContainerRef = useRef<HTMLDivElement>(null);
    const scrollPosition = useRef(0);
    const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
    const isAddingTask = addingTaskDate === date.format('YYYY-MM-DD');
    const autoScrollInterval = useRef<NodeJS.Timeout | null>(null);
    const lastScrollTime = useRef(0);

    const { findProject, todoProjects, loadMoreOverdueTasks, isFetchingMoreOverdueTasks } =
      useTodoTasksContext();
    const { taskSettings } = useTaskSettings();
    const { findPhaseForTaskTime } = useCircadianContext();

    const hasNoProjects = !todoProjects.length;
    const dateLocale = getDayjsLocaleForLanguage(language);
    const formattedDate = date.locale(dateLocale).format('MMM DD');
    const formattedWeekday = date.locale(dateLocale).format('dddd');

    useEffect(() => {
      if (listRef.current) {
        listRef.current.scrollTop = scrollPosition.current;
      }
    }, [tasks]);

    // Handle scroll for infinite loading on overdue list
    useEffect(() => {
      if (!isOverDue || !tasksContainerRef.current) {
        return;
      }

      const handleScroll = () => {
        const container = tasksContainerRef.current;
        if (!container || isFetchingMoreOverdueTasks) {
          return;
        }

        const { scrollTop, scrollHeight, clientHeight } = container;
        const threshold = 200; // Load more when 200px from bottom

        if (scrollHeight - scrollTop - clientHeight < threshold) {
          loadMoreOverdueTasks();
        }
      };

      const container = tasksContainerRef.current;
      container.addEventListener('scroll', handleScroll, { passive: true });

      return () => {
        container.removeEventListener('scroll', handleScroll);
      };
    }, [isOverDue, isFetchingMoreOverdueTasks, loadMoreOverdueTasks]);

    const { sortedTasks, lastSortIndexOfIncomplete } = useMemo(() => {
      if (isOverDue) {
        return { sortedTasks: tasks, lastSortIndexOfIncomplete: 0 };
      }

      // First sort by completed status
      const completedTasks = tasks.filter((t) => t.completed);
      const incompleteTasks = tasks.filter((t) => !t.completed);

      // Sort each group by sortIndex
      const sortByIndex = (tasks: TaskItem[]) => {
        return tasks.sort((a, b) => {
          if (a.sortIndex === undefined && b.sortIndex === undefined) return 0;
          if (a.sortIndex === undefined) return 1;
          if (b.sortIndex === undefined) return -1;
          return a.sortIndex - b.sortIndex;
        });
      };

      const sortedIncomplete = sortByIndex(incompleteTasks);
      const sortedCompleted = sortByIndex(completedTasks);
      const sortedTasks = [...sortedIncomplete, ...sortedCompleted];

      const lastSortIndexOfIncomplete =
        last(sortedIncomplete.filter((i) => !!i.sortIndex))?.sortIndex ?? 0;

      return { sortedTasks, lastSortIndexOfIncomplete };
    }, [tasks, isOverDue]);

    const totalTaskMinutes = useMemo(() => {
      return tasks.reduce((sum, task) => {
        const durationMinutes = task.duration?.amount ?? taskSettings.durationMin;
        return sum + durationMinutes;
      }, 0);
    }, [tasks, taskSettings.durationMin]);

    const completedTaskMinutes = useMemo(() => {
      return tasks.reduce((sum, task) => {
        if (!task.completed) {
          return sum;
        }

        const durationMinutes = task.duration?.amount ?? taskSettings.durationMin;
        return sum + durationMinutes;
      }, 0);
    }, [tasks, taskSettings.durationMin]);

    const totalTaskDurationLabel = useMemo(() => {
      return translateMinToHHhMMmString(totalTaskMinutes);
    }, [totalTaskMinutes]);

    const completedTaskDurationLabel = useMemo(() => {
      return translateMinToHHhMMmString(completedTaskMinutes);
    }, [completedTaskMinutes]);

    const totalTaskCompletionPercent = useMemo(() => {
      if (!totalTaskMinutes) {
        return 0;
      }

      return Math.min(100, (completedTaskMinutes / totalTaskMinutes) * 100);
    }, [completedTaskMinutes, totalTaskMinutes]);

    const storeScrollPosition = () => {
      if (listRef.current) {
        scrollPosition.current = listRef.current.scrollTop;
      }
    };

    const [{ isOver, canDrop }, drop] = useDrop(
      () => ({
        accept: 'TASK',
        drop: (item: TaskItem, monitor) => {
          if (monitor.didDrop() || dragOverIndex === null) {
            return;
          }
          const isDraggedToOtherDay =
            getDueDateYYYYMMDD(item.dueDateTime) !== date.format('YYYY-MM-DD');

          if (isDraggedToOtherDay) {
            const newSortIndex = calculateNewSortIndex(sortedTasks, dragOverIndex);
            onMoveTask(item, newSortIndex);
          } else {
            // Find the current index of the task in the sorted tasks
            const currentIndex = sortedTasks.findIndex((t) => t.taskId === item.taskId);

            // Only call onMoveTask if the position has changed and task is not completed
            // Since the dragged task is hidden, we need to compare with the adjusted dragOverIndex
            const isSamePosition =
              currentIndex === dragOverIndex || currentIndex === dragOverIndex - 1;

            if (
              !isSamePosition &&
              !item.completed &&
              !(currentIndex === 0 && dragOverIndex === 0)
            ) {
              const newSortIndex = calculateNewSortIndex(sortedTasks, dragOverIndex);
              onMoveTask(item, newSortIndex);
            }
          }
          setDragOverIndex(null);
        },
        hover: (item: TaskItem, monitor) => {
          if (!listRef.current) return;

          const clientOffset = monitor.getClientOffset();
          if (!clientOffset) return;

          const taskElements = listRef.current.getElementsByClassName('kanban-task-card');

          let newDragOverIndex = 0;
          let minDistance = Infinity;

          // Check if we're before the first task
          if (taskElements.length > 0) {
            const firstTaskRect = taskElements[0].getBoundingClientRect();
            const distanceToFirst = Math.abs(firstTaskRect.top - clientOffset.y);
            minDistance = distanceToFirst;
            newDragOverIndex = 0;
          }

          // Check distances to all task middle points
          for (let i = 0; i < taskElements.length; i++) {
            const taskElement = taskElements[i];
            const taskRect = taskElement.getBoundingClientRect();
            const taskMiddleY = taskRect.top + (taskRect.bottom - taskRect.top) / 2;
            const distance = Math.abs(taskMiddleY - clientOffset.y);

            if (distance < minDistance) {
              minDistance = distance;
              newDragOverIndex = i + 1; // +1 because we want to insert after this task
            }
          }

          // Check if we're after the last task
          if (taskElements.length > 0) {
            const lastTaskRect = taskElements[taskElements.length - 1].getBoundingClientRect();
            const distanceToLast = Math.abs(lastTaskRect.bottom - clientOffset.y);
            if (distanceToLast < minDistance) {
              newDragOverIndex = taskElements.length;
            }
          }

          if (newDragOverIndex !== dragOverIndex) {
            setDragOverIndex(newDragOverIndex);
          }
        },
        canDrop: (item: TaskItem) =>
          !isOverDue && (item.appFrom !== 'apple-reminders' || isAppleRemindersDesktopSupported()),
        collect: (monitor) => ({
          isOver: monitor.isOver(),
          canDrop: monitor.canDrop(),
        }),
      }),
      [onMoveTask, dragOverIndex, sortedTasks]
    );

    const handleTaskComplete = async (
      task: TaskItem,
      taskId: string,
      completed: boolean,
      parentOriginalTaskId?: string
    ) => {
      try {
        await onCompleteTask(task, taskId, completed, {
          date: date.format('YYYY-MM-DD'),
          parentOriginalTaskId,
        });
      } catch (error) {
        console.error('Failed to update task status:', error);
      }
    };

    const handleDragOver = (e: React.DragEvent) => {
      if (!tasksContainerRef.current) return;

      const containerRect = tasksContainerRef.current.getBoundingClientRect();
      const scrollThreshold = 100; // pixels from edge to trigger scroll
      const scrollSpeed = 15; // pixels per interval
      const now = Date.now();

      // Prevent too frequent updates
      if (now - lastScrollTime.current < 16) return;
      lastScrollTime.current = now;

      // Clear any existing interval
      if (autoScrollInterval.current) {
        clearInterval(autoScrollInterval.current);
      }

      const { scrollTop, scrollHeight, clientHeight } = tasksContainerRef.current;
      const maxScroll = scrollHeight - clientHeight;
      const isAtTop = scrollTop <= 0;
      const isAtBottom = scrollTop >= maxScroll;

      // Check if we're near the edges
      if (e.clientY < containerRect.top + scrollThreshold && !isAtTop) {
        // Scroll up
        autoScrollInterval.current = setInterval(() => {
          if (tasksContainerRef.current) {
            const newScrollTop = Math.max(0, tasksContainerRef.current.scrollTop - scrollSpeed);
            tasksContainerRef.current.scrollTop = newScrollTop;

            // Stop scrolling if we've reached the top
            if (newScrollTop <= 0) {
              clearInterval(autoScrollInterval.current!);
              autoScrollInterval.current = null;
            }
          }
        }, 16);
      } else if (e.clientY > containerRect.bottom - scrollThreshold && !isAtBottom) {
        // Scroll down
        autoScrollInterval.current = setInterval(() => {
          if (tasksContainerRef.current) {
            const newScrollTop = Math.min(
              maxScroll,
              tasksContainerRef.current.scrollTop + scrollSpeed
            );
            tasksContainerRef.current.scrollTop = newScrollTop;

            // Stop scrolling if we've reached the bottom
            if (newScrollTop >= maxScroll) {
              clearInterval(autoScrollInterval.current!);
              autoScrollInterval.current = null;
            }
          }
        }, 16);
      }
    };

    const handleDragEnd = () => {
      if (autoScrollInterval.current) {
        clearInterval(autoScrollInterval.current);
        autoScrollInterval.current = null;
      }
    };

    const handleCreateTask = async (task: TaskItem) => {
      setAddingTaskDate(null);
      const selectedProject = todoProjects.find((p) => p.todoTaskId === task.boardId);

      const defaultDueDateTime = {
        date: task.dueDateTime?.date || dayjs(date).format('YYYY-MM-DD'),
        datetime: task.dueDateTime?.datetime,
        timezone: dayjs.tz.guess(),
      };
      const defaultDuration = task.duration || {
        amount: task.duration?.amount || taskSettings.durationMin,
        unit: 'minute' as const,
      };

      onCreateTask({
        name: task.name,
        desc: '',
        ...getFormattedDueDateForAddTask('due', defaultDueDateTime),
        ...getStartDateTimeFromDuration(task.duration || defaultDuration, defaultDueDateTime),
        appFrom: selectedProject?.appFrom || 'lifestack',
        duration: task.duration || defaultDuration,
        addedToCalendar: false,
        completed: false,
        sortIndex: task.sortIndex,
        updatedAt: new Date().toISOString(),
        boardId: task.boardId,
        recurring:
          task.recurring ||
          ({
            isRecurring: false,
            string: '',
            lang: 'en',
            timezone: defaultDueDateTime.timezone,
          } as TaskItem['recurring']),
      });
      setAddingTaskDate(date.format('YYYY-MM-DD')); // Create another empty task
    };

    useEffect(() => {
      return () => {
        if (autoScrollInterval.current) {
          clearInterval(autoScrollInterval.current);
        }
      };
    }, []);

    const getPhaseForTask = useCallback(
      (task: TaskItem) => {
        const dueDate = task.dueDateTime?.datetime;

        if (!dueDate) {
          return;
        }
        const startDueDate = getStartDateTimeFromDuration(
          task.duration || { amount: taskSettings.durationMin, unit: 'minute' },
          task.dueDateTime
        )?.startDateTime;

        if (!startDueDate) {
          return;
        }
        return findPhaseForTaskTime(startDueDate.datetime, dueDate);
      },
      [findPhaseForTaskTime, taskSettings]
    );

    if (isOverDue && isCollapsed) {
      return (
        <div className={clsx('kanban-list', 'kanban-list--collapsed', className)} style={style}>
          <div className="kanban-list__collapsed-container">
            <IconButton size="small" onClick={onToggleCollapse}>
              <KeyboardTabIcon className="kanban-list__expand-icon" fontSize="small" />
            </IconButton>
            <div className="kanban-list__collapsed-title">
              <span>{t('task.sections.overdue')}</span>
              <span className="kanban-list__collapsed-title__date">{formattedDate}</span>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div ref={drop} className={clsx('kanban-list', className)} style={style}>
        <div className="kanban-list__container">
          <div className="kanban-list__header">
            <div className="kanban-list__header__left-group">
              <div className="kanban-list__header__day">
                {isOverDue && (
                  <IconButton size="small" onClick={onToggleCollapse}>
                    <KeyboardTabIcon className="kanban-list__overdue-icon" fontSize="small" />
                  </IconButton>
                )}
                {isOverDue ? t('task.sections.overdue') : formattedWeekday}
              </div>
              <div className="kanban-list__header__meta">
                <div className="kanban-list__header__date">
                  {isOverDue && '- '}
                  {formattedDate}
                </div>
              </div>
            </div>
            {!!filterOptions.length && <KanbanListFilterMenu filterOptions={filterOptions} />}
            {!isOverDue && (
              <CustomTooltip
                title={
                  <div className="kanban-list__header__total-hours-tooltip">
                    <div className="kanban-list__header__total-hours-tooltip__title">
                      <span className="kanban-list__header__total-hours-indicator">
                        <CircularProgress
                          variant="determinate"
                          value={totalTaskCompletionPercent}
                          size={16}
                          thickness={5}
                        />
                      </span>{' '}
                      {t('task.kanban.totalTaskHours')}
                    </div>
                    <div className="kanban-list__header__total-hours-tooltip__description">
                      {t('task.kanban.totalTaskHoursDescription', {
                        completed: completedTaskDurationLabel,
                        total: totalTaskDurationLabel,
                      })}
                    </div>
                  </div>
                }
                placement="top"
              >
                <span className="kanban-list__header__total-hours-pill">
                  <Typography variant="body2" className="kanban-list__header__total-hours">
                    {completedTaskDurationLabel}
                  </Typography>
                  <span className="kanban-list__header__total-hours-indicator">
                    <CircularProgress
                      variant="determinate"
                      value={totalTaskCompletionPercent}
                      size={16}
                      thickness={5}
                    />
                  </span>
                </span>
              </CustomTooltip>
            )}
          </div>
          <div className="kanban-list__item-group" ref={listRef}>
            {isFetchingTasks && <CircularProgress className="kanban-list__loading" size={30} />}
            {!isFetchingTasks && (isDemo || !hasNoProjects || sortedTasks.length > 0) && (
              <div
                className="kanban-list__item-group__tasks"
                ref={tasksContainerRef}
                onDragOver={handleDragOver}
                onDragEnd={handleDragEnd}
              >
                {dragOverIndex === 0 && isOver && canDrop && (
                  <div className="kanban-list__preview-card" />
                )}
                {sortedTasks
                  .filter((t) => !!t)
                  .map((task, index) => (
                    <React.Fragment key={task.taskId}>
                      <KanbanTaskCard
                        task={task}
                        phase={getPhaseForTask(task)}
                        isOverDue={isOverDue}
                        projectName={
                          isAssignedTeamTask(task)
                            ? task.assignedTeamTask.project.name
                            : isDemo
                              ? DEMO_TASK_PROJECT_NAME
                              : findProject(task.boardId!)?.name
                        }
                        onTaskCreate={onCreateTask}
                        onTaskUpdate={onUpdateTask}
                        onTaskDelete={(taskId, boardId, parentOriginalTaskId) =>
                          onDeleteTask(
                            {
                              ...task,
                              taskId,
                              boardId,
                            },
                            {
                              date: getDueDateYYYYMMDD(task.dueDateTime),
                              isOverdue: getIsOverdue(
                                task.dueDateTime?.datetime || task.dueDateTime?.date
                              ),
                              parentOriginalTaskId,
                            }
                          )
                        }
                        onClick={() => {
                          if (isDemo) return;
                          storeScrollPosition();
                          onClickTask(task);
                          trackEventMixpanel('open_task_detail_modal', {
                            taskId: task.taskId,
                          });
                        }}
                        onTaskComplete={(taskId, completed, parentOriginalTaskId) => {
                          storeScrollPosition();
                          handleTaskComplete(task, taskId, completed, parentOriginalTaskId);
                        }}
                      />
                      {dragOverIndex === index + 1 && isOver && canDrop && (
                        <div className="kanban-list__preview-card" />
                      )}
                    </React.Fragment>
                  ))}
                {isOverDue && isFetchingMoreOverdueTasks && (
                  <div className="kanban-list__loading-more">
                    <CircularProgress size={20} />
                  </div>
                )}
              </div>
            )}
            {!isFetchingTasks &&
              !isOverDue &&
              (isAddingTask ? (
                <TaskCreateCard
                  task={
                    {
                      sortIndex: lastSortIndexOfIncomplete + SORT_INDEX_INCREMENT,
                    } as TaskItem
                  }
                  date={date.format('YYYY-MM-DD')}
                  onSave={handleCreateTask}
                  onCancel={() => setAddingTaskDate(null)}
                />
              ) : (
                <button
                  className="kanban-list__add-task"
                  data-testid="add-task-button"
                  onClick={() => setAddingTaskDate(date.format('YYYY-MM-DD'))}
                  disabled={isDemo}
                >
                  <AddOutlined className="kanban-list__add-task__icon" />
                  <Typography variant="body2" className="kanban-list__add-task__text">
                    {t('task.sections.addTask')}
                  </Typography>
                </button>
              ))}
          </div>
        </div>
      </div>
    );
  }
);
